import { ReactNode } from "react";
import styled, { css } from "../styles";
import { Flex } from "./Flex";
import Loading from "./Loading";

export interface Column<T> {
  header: string;
  key: string;
  width?: number;
  noPadding?: boolean;

  render?: (item: T) => ReactNode;
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (item: T) => void;
  lastElementRef?: (node: HTMLTableRowElement | null) => void;
  transparentHeader?: boolean;
  headerFontSize?: "s";
  isFetching?: boolean;
  padding?: "s";
}

export const Table = <T extends {}>({
  columns,
  data,
  onRowClick,
  lastElementRef,
  transparentHeader,
  headerFontSize,
  isFetching,
  padding,
}: TableProps<T>) => {
  return (
    <TableWrap>
      <StyledTable
        transparentHeader={transparentHeader}
        headerFontSize={headerFontSize}
        padding={padding}
      >
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                style={{
                  width: column.width ? `${column.width}px` : "130px",
                  padding: column.noPadding === true ? "0" : undefined,
                }}
                scope="col"
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <Row
              key={index}
              onClick={() => onRowClick?.(item)}
              hasRowClick={!!onRowClick}
              ref={index === data.length - 5 ? lastElementRef : null}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  style={{
                    width: column.width ? `${column.width}px` : "130px",
                    padding: column.noPadding === true ? "0" : undefined,
                  }}
                >
                  {column.render
                    ? column.render(item)
                    : (item as Record<string, any>)[column.key]}
                </td>
              ))}
            </Row>
          ))}
        </tbody>
      </StyledTable>
      {isFetching && (
        <Flex align="center" justify="center" style={{ padding: "16px 0" }}>
          <Loading />
        </Flex>
      )}
    </TableWrap>
  );
};

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const StyledTable = styled.table<{
  transparentHeader?: boolean;
  padding?: "s";
  headerFontSize?: "s";
}>`
  width: 100%;
  border-spacing: 0;
  border-collapse: separate;
  box-sizing: border-box;

  thead,
  tbody tr {
    display: table;
    min-width: 100%;
    table-layout: fixed;
  }

  tbody {
    display: block;
    overflow-y: scroll;
    min-width: 100%;
  }

  th,
  td {
    text-align: left;
    padding: ${(p) =>
      p.padding === "s"
        ? `${p.theme.spacing.s} ${p.theme.spacing.l}`
        : `${p.theme.spacing.m} ${p.theme.spacing.xl}`};
    border-bottom: 1px solid ${(p) => p.theme.color.card.divider};
  }

  ${(p) =>
    p.transparentHeader &&
    css`
      th {
        border-bottom: 1px solid transparent;
      }
    `}

  th {
    background-color: ${(p) =>
      p.transparentHeader ? "transparent" : p.theme.color.card.callout};
    font-size: ${(p) =>
      p.headerFontSize === "s"
        ? p.theme.typography.size.xxs
        : p.theme.typography.size.s};
    font-weight: ${(p) => p.theme.typography.weight.bold};
    color: ${(p) => p.theme.color.typography.secondary};
    text-transform: uppercase;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  tr:last-of-type {
    td {
      border-bottom: none;
    }
  }
`;

const Row = styled.tr<{ hasRowClick: boolean }>`
  display: table;
  width: 100%;
  table-layout: fixed;
  background-color: ${(p) => p.theme.color.card.background};

  ${(p) =>
    p.hasRowClick &&
    css`
      cursor: pointer;
      :hover {
        background-color: ${(p) => p.theme.color.card.callout};
      }
    `}
`;
